import React, {useCallback, useEffect, useState} from 'react';
import styled, {useTheme} from 'styled-components/native';
import {Theme} from '../../../../theme/lightTheme';
import {useSafeAreaInsets} from 'react-native-safe-area-context';
import {getHomeTabItemByKey, getSettingByKey} from '../../../../utils/helpers';
import {RootState} from '../../../../redux/store';
import {useSelector} from 'react-redux';
import {
  FilterItemType,
  FilterType,
} from '../../../../interfaces/reducers/homeState';
import {EmptyContainer, Space} from '../../home';
import {useCustomHeaderHeight} from '../../../../utils/hooks/useHeaderHeight';
import useResponsiveDimensions from '../../../../utils/hooks/useResponsiveDimensions';
import {DrugsDiseasesTabProps} from '../../../../interfaces/screens/drugsDiseasesTab';
import Animated, {
  FadeIn,
  FadeInDown,
  FadeInUp,
  Layout,
  SlideInRight,
  useAnimatedStyle,
  useSharedValue,
  withSpring,
} from 'react-native-reanimated';
import AlphabetItem from '../../../../components/alphabetItem';
import {tr} from '../../../../translations';
import TextGeneric from '../../../../components/textGeneric';
import Icon from '../../../../components/icon';
import SelectDrugDisease from '../../../../components/selecteDrugDisease';
import {ChatType, SettingKey, TabKey} from '../../../../utils/enums';
import {getNews} from '../../tabNews/actions';
import {DrugItemType} from '../../../../interfaces/api/tabs';
import ChatFooter from '../../../../components/chat/chatFooter';
import useChatVoiceRecognition from '../../../../utils/hooks/useChatVoiceRecognition';
import RecordView from '../../../../components/chat/recordView';
import GenericButton from '../../../../components/button';
import {TibButton} from '../../../../components/tibButton';
import {resetChat, sendMessage} from '../../chat/actions';
import userVoice from '../../../../utils/services/userVoice';
import screens from '../../../../config/screens';
import {getTabIdByValue} from '../../../../utils/getCategoriesFromTabs';
import {NavigationProp, useNavigation} from '@react-navigation/native';
import Loader from '../../../../components/loader';
import EmptyList from '../../../../components/emptyList';
import {getHomeTabs} from '../../home/actions';
import {BackHandler, Platform} from 'react-native';
import {isWeb} from '../../../../utils/responsive';
import '../../../../utils/webAnimations';

const Container = styled.ScrollView<{headerHeight: number}>`
  width: 100%;
  padding-top: ${({headerHeight}) => headerHeight}px;
  padding-bottom: ${({headerHeight}) => headerHeight}px;
  overflow-y: scroll;
`;

const FilterTitle = styled(TextGeneric)`
  font-size: ${({theme}) => theme.text.s6}px;
  color: ${({theme}) => theme.text.dark};
  margin-top: ${({theme}) => theme.text.s55}px;
  width: 94%;
  align-self: center;
`;

const AlphabetContainer = styled.View`
  width: 94%;
  align-self: center;
  margin-top: ${({theme}) => theme.text.s8}px;
  align-items: center;
  justify-content: center;
  flex-direction: row;
  flex-wrap: wrap;
  gap: ${({theme}) => theme.text.s12 / 2}px;
  margin-left: -${({theme}) => theme.text.s12 / 4}px;
  margin-right: -${({theme}) => theme.text.s12 / 4}px;
`;

const FilterAlphabetButton = styled.TouchableOpacity<{
  paddingHorizontal: number;
}>`
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  padding-horizontal: ${({paddingHorizontal}) => paddingHorizontal}px;
  padding-vertical: ${({theme}) => theme.text.s9}px;
`;

const FilterAlphabetIconContainer = styled.View`
  width: ${({theme}) => theme.text.s1 * 1.3}px;
  height: ${({theme}) => theme.text.s1 * 1.3}px;
  background-color: ${({theme}) => theme.screenBackground};
  border-radius: 100px;
  align-items: center;
  justify-content: center;
  margin-right: ${({theme}) => theme.text.s12}px;
`;

/**
 * TipButtonContainer - Completely isolated tip button component with its own state
 */
const TipButtonContainer = React.memo(() => {
  const [isTipVisible, setIsTipVisible] = useState(false);
  const {wdp, getByScreenSize} = useResponsiveDimensions();
  const theme = useTheme();

  const handleDismissTip = useCallback(() => {
    setIsTipVisible(false);
  }, []);

  const disclaimerButton = (
    <GenericButton
      label={tr('latest.disclaimer')}
      labelStyle={{
        color: theme.text.dark,
        fontSize: theme.text.s6,
      }}
      onPress={() => setIsTipVisible(true)}
      style={{
        marginVertical: getByScreenSize(wdp(5), wdp(2)),
        marginHorizontal: getByScreenSize(wdp(3), wdp(2)),
        backgroundColor: theme.primary + '33',
      }}
      showRightIcon
      rightIconName={'info'}
      rightIconColor={theme.primary}
      rightIconSize={theme.text.s3}
    />
  );

  return (
    <>
      <Animated.View
        entering={FadeIn.delay(500).duration(300)}
        style={{
          alignSelf: 'flex-end',
          width: getByScreenSize('50%', '25%'),
          marginHorizontal: theme.text.s55,
        }}>
        {disclaimerButton}
      </Animated.View>

      {isTipVisible && (
        <TibButton
          isVisible={true}
          onDismiss={handleDismissTip}
          tipText={
            getSettingByKey(SettingKey.DrugsDisclaimer) ||
            tr('drugsAndDiseases.tip')
          }
        />
      )}
    </>
  );
});

// Wrap components that need animations
const AnimatedIcon = Animated.createAnimatedComponent(
  FilterAlphabetIconContainer,
);

/**
 * DrugsDiseasesTab screen component.
 *
 * @param {DrugsDiseasesTabProps} props - The screen component props.
 */
const DrugsDiseasesTab: React.FC<DrugsDiseasesTabProps> = ({
                                                             tabKey,
                                                             topComponent,
                                                             onRecordingStateChange,
                                                           }: DrugsDiseasesTabProps) => {
  const theme = useTheme() as Theme;
  const navigation = useNavigation<NavigationProp<any>>();
  const {bottom: bottomInset} = useSafeAreaInsets();
  const headerHeight = useCustomHeaderHeight();
  const {wdp, hdp, getByScreenSize} = useResponsiveDimensions();
  const pageSize = getByScreenSize(30, 40);
  const {
    tabs,
    isLoading: isTabLoading,
    isError: isTabError,
  } = useSelector((state: RootState) => state.homeTabs);
  const {tabs_with_categories} = useSelector(
    (state: RootState) => state.auth.config,
  );
  const qaTabId = getTabIdByValue(tabs_with_categories, 'qa');
  const [tabFilters, setTabFilters] = useState<FilterType>();
  const [selected, setSelected] = useState<FilterItemType | null>(null);
  const [data, setData] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [page, setPage] = useState(1);
  const [isLastPage, setIsLastPage] = useState<boolean>(false);
  const [question, setQuestion] = useState('');
  const {isRecording, startListening, stopListening} =
    useChatVoiceRecognition(setQuestion);

  // Simple scale animation value
  const iconScale = useSharedValue(1);

  // Handle back press from detail screen
  const handleBackPress = () => {
    if (selected) {
      setSelected(null);
      return true;
    }
    return false;
  };

  // Set up the back button listener
  useEffect(() => {
    if (Platform.OS !== 'web') {
      const backHandlerSubscription = BackHandler.addEventListener(
        'hardwareBackPress',
        handleBackPress,
      );

      return () => {
        backHandlerSubscription.remove();
      };
    }
  }, [handleBackPress]);

  const initTabInfo = () => {
    const homeTabItem = getHomeTabItemByKey(tabKey);
    const letterFilterIdx = (homeTabItem?.filters || []).findIndex(
      i => i.key === 'letter_id',
    );
    if (letterFilterIdx > -1) {
      setTabFilters(homeTabItem?.filters?.[letterFilterIdx]);
    }
  };

  const loadMore = async (refresh = false) => {
    if (isLoading && !refresh) {
      return;
    }
    setIsError(false);
    if (!isLastPage || refresh) {
      if (refresh) {
        setData([]);
      }
      setIsLoading(true);
      const result = await getNews({
        tab_id: getHomeTabItemByKey(tabKey)?.id,
        page: refresh ? 1 : page,
        // ...(search ? {keywords: search} : {}),
        ...(selected ? {letter_id: selected?.id} : {}),
        pageSize,
      });

      if (!result) {
        setIsError(true);
        setIsLoading(false);
        setIsLastPage(true);
        return;
      }
      setData(prev => {
        if (refresh) {
          return result?.data || [];
        } else {
          return [...prev, ...(result?.data || [])].filter(
            (value, index, self) => {
              return (
                self.findIndex(obj =>
                  ['id'].every(key => obj[key] === value[key]),
                ) === index
              );
            },
          );
        }
      });

      setPage(prev => (refresh ? 2 : prev + 1));
      const totalPageCount = Math.ceil(
        parseFloat(result?.total + '' || '2') / pageSize,
      );
      setIsLastPage((refresh ? 1 : page) >= totalPageCount);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadMore(true);
  }, [selected]);

  useEffect(() => {
    initTabInfo();
  }, [tabs]);

  // Forward isRecording state to parent component when it changes
  useEffect(() => {
    if (onRecordingStateChange) {
      onRecordingStateChange(isRecording);
    }
  }, [isRecording, onRecordingStateChange]);

  // Define animated style for icon scaling
  const iconStyle = useAnimatedStyle(() => ({
    transform: [{scale: iconScale.value}],
  }));

  // Handle filter button press with animation
  const handleFilterPress = () => {
    // Pulse animation effect
    iconScale.value = withSpring(1.2, {damping: 10}, () => {
      iconScale.value = withSpring(1);
    });
    setSelected(null);
  };

  // Render each alphabet item
  const renderAlphabetItem = (item: FilterItemType, index: number) => (
    <Animated.View
      key={item?.id || `alphabet-${index}`}
      {...(Platform.OS === 'web'
        ? {className: 'screen-fade-in-up'}
        : {
            entering: FadeInUp.duration(300)
              .delay(50 + index * 15)
              .springify(),
          })}>
      <AlphabetItem
        value={item?.value}
        onPress={() => setSelected(item)}
        isSelected={selected?.id === item?.id}
      />
    </Animated.View>
  );

  // Layout animation configuration
  const layoutAnimation = Layout.springify().damping(12);

  const onDrugDiseaseSelectedPressHandler = (item: DrugItemType) => {
    // setSelected(null);
    navigation?.navigate(screens.content.drugsDiseasesDetails, {
      tabKey,
      id: item?.id,
      title: item?.name,
    });
  };

  const onCheckAnswerPressed = async () => {
    resetChat();
    userVoice?.stop();
    stopListening();
    setQuestion('');
    sendMessage({question, type: ChatType.QNA, tab_id: qaTabId});
    navigation?.navigate(screens.content.genioChatRoutes, {
      screen: screens.content.genioChat,
    });
    setQuestion('');
  };

  return (
    <>
      <RecordView
        isRecording={isRecording}
        question={question}
        onClose={stopListening}
        onSend={onCheckAnswerPressed}
        contentContainerStyle={{
          paddingBottom: bottomInset + hdp(14),
        }}
      />
      <Container
        style={{
          height: isWeb ? hdp(100) : 'auto',
        }}
        headerHeight={headerHeight}>
        {topComponent && topComponent()}

        {isTabError ? (
          <EmptyContainer hdp={hdp}>
            <EmptyList
              iconName={'alert'}
              iconColor={theme.primary}
              iconSize={theme.text.s1 * 2.5}
              onRefreshPress={getHomeTabs}
              title={'app.error'}
              titleStyle={{color: theme.text.black}}
              showRefresh
            />
          </EmptyContainer>
        ) : isTabLoading ? (
          <EmptyContainer hdp={hdp}>
            <Loader />
          </EmptyContainer>
        ) : (
          <>
            <Animated.View
              layout={layoutAnimation}
              style={{
                width: getByScreenSize('100%', '90%'),
                alignSelf: 'center',
              }}>
              {selected ? (
                // Selected state view - shows filter button
                <Animated.View
                  key="filter-view"
                  layout={layoutAnimation}
                  {...(Platform.OS === 'web'
                    ? {className: 'screen-fade-in-up'}
                    : {entering: FadeInUp.duration(300).springify()})}>
                  <FilterAlphabetButton
                    paddingHorizontal={wdp(getByScreenSize(4, 1))}
                    onPress={handleFilterPress}>
                    <AnimatedIcon style={iconStyle}>
                      <Icon
                        name={'filterSolid'}
                        size={20}
                        color={theme.settings.icon}
                      />
                    </AnimatedIcon>
                    <Animated.View
                      style={{flex: 1}}
                      entering={SlideInRight.duration(400)}>
                      <FilterTitle style={{marginTop: 0}} bold>
                        {tr('drugsAndDiseases.filterByAlphabet')}
                      </FilterTitle>
                    </Animated.View>
                  </FilterAlphabetButton>
                  <Animated.View
                    style={{flex: 1}}
                    entering={FadeInDown.duration(400)}>
                    <SelectDrugDisease
                      data={data || []}
                      title={tr(
                        `drugsAndDiseases.${tabKey === TabKey.Diseases ? 'selectDiseases' : 'selectDrug'}`,
                      )}
                      onRefresh={() => loadMore(true)}
                      onEndReached={() => loadMore()}
                      isLoading={isLoading}
                      isError={isError}
                      onDrugDiseaseSelected={onDrugDiseaseSelectedPressHandler}
                    />
                  </Animated.View>
                </Animated.View>
              ) : (
                // Unselected state view - shows alphabet grid
                <Animated.View
                  key="alphabet-view"
                  layout={layoutAnimation}
                  entering={FadeIn.duration(300).springify()}>
                  <FilterTitle semiBold>
                    {tr('drugsAndDiseases.findPart1') +
                      ' ' +
                      tr(`drugsAndDiseases.${tabKey}`) +
                      ' ' +
                      tr('drugsAndDiseases.findPart2')}
                  </FilterTitle>
                  <Animated.View
                    {...(Platform.OS === 'web'
                      ? {className: 'screen-fade-in-up'}
                      : {entering: FadeInUp.duration(400).delay(100)})}>
                    <AlphabetContainer>
                      {(tabFilters?.items || []).map(renderAlphabetItem)}
                    </AlphabetContainer>
                  </Animated.View>
                </Animated.View>
              )}
            </Animated.View>
            <Animated.View
              style={{
                marginTop: theme.text.s55,
              }}
              layout={layoutAnimation}>
              <ChatFooter
                isRow
                value={question}
                onChangeText={setQuestion}
                onAttachPress={startListening}
                isAttachBadge={isRecording}
                hideFocusedEffect
                placeholder={
                  tabKey === TabKey.Drugs
                    ? tr('drugsAndDiseases.askDrug')
                    : tr('drugsAndDiseases.askDisease')
                }
                onSubmit={onCheckAnswerPressed}
                isRecording={isRecording}
                hideAnimation
                style={{
                  width: '94%',
                  alignSelf: 'center',
                }}
              />
            </Animated.View>
            <Animated.View
              style={{
                width: getByScreenSize('100%', '90%'),
                alignSelf: 'center',
              }}
              layout={layoutAnimation}>
              <TipButtonContainer />
            </Animated.View>
          </>
        )}
        <Space bottomInset={bottomInset} extraSpace={hdp(11)} />
      </Container>
    </>
  );
};

export default DrugsDiseasesTab;
